<template>
  <div>
    <header class="section-header section-header--lg mt-3">
      <div>
        <h1 class="heading" qaid="registration-form-page-title" v-html="bp('completeRegistration')" />
      </div>
    </header>

    <p class="mb-3" qaid="registration-form-page-description" v-html="bp('customerDetails')" />
    <div class="grid-x grid-margin-x">
      <div class="cell medium-6">
        <form
          id="product-registration-form"
          ref="productRegistrationForm"
          :action="formActionUrl"
          method="post"
          qaid="registration-form"
          @submit.prevent="validateAndSubmit"
        >
          <fieldset class="max-w-sm">
            <input
              id="bike_description"
              v-model="selectedBike.description"
              name="bikeDescription"
              type="hidden"
              qaid="registration-form-bike-description"
            />
            <input type="hidden" name="ignoreRedirectUrl" :value="ignoreRedirectUrl" />
            <input type="hidden" name="ignoreRedirectMode" value="Auto" />
            <input
              v-if="b2b"
              id="trek_retailer"
              v-model="currentBikeShop"
              name="trekRetailer"
              type="hidden"
              qaid="registration-form-current-bike-shop"
            />

            <input
              id="validBikeJson"
              name="validBikeJson"
              type="hidden"
              qaid="registration-form-selected-bike"
              :value="JSON.stringify(selectedBike)"
            />

            <div id="purchase-date-field" class="form-group" :class="datePickerContainerClass">
              <label class="font-medium" v-html="bp('purchaseDate')" />
              <date-picker
                id="purchase-date-input"
                ref="purchaseDatePicker"
                class="mb-0"
                qaid="purchase-date-input"
                name="purchaseDate"
                :allow-input="true"
                @change="setDatePicker"
                @blur="checkDatePicker"
              />
              <div v-if="datePickerContainerClass === 'has-error'" class="form-feedback is-invalid">
                The {{ bp('purchaseDate') }} field is required.
              </div>
            </div>

            <form-group
              id="serial_number"
              v-model="serialNumberField"
              v-validate="'required'"
              type="text"
              :data-vv-as="bp('serialNumber')"
              :error="bp('serialNumberError')"
              name="serialNumber"
              :label="bp('serialNumber')"
              :show-error-messages="fieldHasError('serialNumber')"
              qaid="registration-form-serial"
              @input="fieldHasError('serialNumber')"
            ></form-group>

            <form-group
              id="first_name"
              v-model="firstNameField"
              v-validate="'required'"
              type="text"
              :data-vv-as="bp('firstName')"
              :error="bp('firstNameError')"
              name="firstName"
              :label="bp('firstName')"
              qaid="registration-form-first-name"
              :show-error-messages="fieldHasError('firstName')"
              @input="fieldHasError('firstName')"
            ></form-group>

            <form-group
              id="last_name"
              v-model="lastNameField"
              v-validate="'required'"
              type="text"
              :data-vv-as="bp('surname')"
              :error="bp('lastNameError')"
              name="lastName"
              :label="bp('surname')"
              :show-error-messages="fieldHasError('lastName')"
              qaid="registration-form-surname"
              @input="fieldHasError('lastName')"
            ></form-group>

            <form-group
              id="email"
              v-model="emailAddressField"
              v-validate="'required|email'"
              type="email"
              :data-vv-as="bp('emailAddress')"
              :error="bp('emailError')"
              name="email"
              :label="bp('emailAddress')"
              :show-error-messages="fieldHasError('email')"
              qaid="registration-form-email"
              @input="fieldHasError('email')"
            ></form-group>

            <form-group
              id="address"
              v-model="addressLine1Field"
              v-validate="'required'"
              type="text"
              :data-vv-as="bp('addressLine1')"
              :error="bp('addressError')"
              name="address"
              :label="bp('addressLine1')"
              :show-error-messages="fieldHasError('address')"
              qaid="registration-form-address"
              @input="fieldHasError('address')"
            ></form-group>

            <form-group
              id="address2"
              v-model="addressLine2Field"
              type="text"
              name="address2"
              :label="bp('addressLine2')"
              qaid="registration-form-address2"
            ></form-group>

            <form-group
              id="city"
              v-model="townCityField"
              v-validate="'required'"
              type="text"
              name="city"
              :label="bp('townCity')"
              :data-vv-as="bp('townCity')"
              :error="bp('cityError')"
              :show-error-messages="fieldHasError('city')"
              qaid="registration-form-city"
              @input="fieldHasError('city')"
            ></form-group>

            <form-group
              id="state"
              v-model="stateField"
              v-validate="'required'"
              type="text"
              name="state"
              :label="bp('selectState')"
              :data-vv-as="bp('selectState')"
              :error="bp('stateError')"
              :show-error-messages="fieldHasError('state')"
              qaid="registration-form-state"
              @input="fieldHasError('state')"
            ></form-group>

            <label for="select" class="form-label">{{ bp('country') }}</label>
            <div class="select mb-3">
              <select
                id="country"
                v-model="countryField"
                v-validate="'required'"
                name="country"
                qaid="registration-form-tech-country-drop-down"
                class="form-control"
                :class="countriesContainerClass"
                @blur="checkCountryField"
              >
                <option
                  v-for="(country, index) in countriesList"
                  :key="index"
                  :value="country"
                  :disabled="index === 0"
                  >{{ country }}</option
                >
              </select>
            </div>

            <form-group
              id="zip"
              v-model="postalCodeField"
              v-validate="'required'"
              type="text"
              :data-vv-as="bp('postalcode')"
              :error="bp('zipError')"
              name="zip"
              :label="bp('postalcode')"
              :show-error-messages="fieldHasError('zip')"
              qaid="registration-form-postal"
              @input="fieldHasError('zip')"
            ></form-group>

            <form-group
              v-if="!b2b"
              id="trekRetailer"
              v-model="trekRetailerField"
              v-validate="'required'"
              type="text"
              :data-vv-as="bp('trekRetailer')"
              :error="bp('trekRetailerError')"
              name="trekRetailer"
              :label="bp('trekRetailer')"
              :show-error-messages="fieldHasError('trekRetailer')"
              qaid="registration-form-retailer"
              @input="fieldHasError('trekRetailer')"
            ></form-group>

            <fieldset class="form-fieldset">
              <label class="checkbox">
                <input
                  id="opt_in"
                  v-model="optInVal"
                  type="checkbox"
                  true-value="true"
                  false-value="false"
                  name="optIn"
                />
                <input id="optInValue" type="hidden" name="optInVal" :value="optInVal" />
                <span class="control-indicator" qaid="registration-form-optin"></span>
                <span v-html="bp('emailSignup')" />
              </label>
              <label v-if="isPrivacyPolicyLocale" class="checkbox">
                <input
                  id="privacyPolicyAcceptValue"
                  v-model="hasAcceptedPrivacyPolicy"
                  type="checkbox"
                  name="hasAcceptedPrivacyPolicy"
                />
                <span class="control-indicator" qaid="registration-form-privacy-accept"></span>
                <span v-html="privacyPolicyText" />
              </label>
            </fieldset>
          </fieldset>
          <div class="buttons py-5">
            <trek-button
              primary
              qaid="registration-form-submit"
              :disabled="!hasAcceptedPrivacyPolicy || hasRecaptchaFailure"
              @click="executeRecaptcha()"
            >
              <span v-html="bp('registerBike')"></span>
            </trek-button>
            <trek-link button secondary href="/" qaid="registration-form-cancel">
              <span v-html="bp('cancel')"></span>
            </trek-link>
          </div>
          <trek-recaptcha
            ref="recaptcha"
            :re-captcha-site-key="reCaptchaSiteKey"
            @tokenSuccess="validateAndSubmit"
            @tokenFailure="tokenFailure"
          ></trek-recaptcha>
          <input id="reCaptchaToken" type="hidden" name="reCaptchaToken" :value="reCaptchaToken" />
        </form>
      </div>
      <div class="cell sm:hidden md:block medium-6">
        <img :src="selectedBike.imageUrl" qaid="registration-form-image" />
      </div>
    </div>
  </div>
</template>

<script>
import FormGroup from '@/components/FormGroup';
import DatePicker from '@/components/DatePicker';
import TrekButton from '@/components/TrekButton';
import TrekLink from '@/components/TrekLink';
import {mapState} from 'vuex';
import TrekRecaptcha from '@/components/TrekRecaptcha';

export default {
  components: {
    TrekButton,
    TrekLink,
    FormGroup,
    DatePicker,
    TrekRecaptcha,
  },
  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    selectedBike: {
      type: Object,
      default: () => ({}),
    },
    currentBikeShop: {
      type: String,
      default: '',
    },
    selectedBikeJson: {
      type: String,
      default: '',
    },
    countries: {
      type: Array,
      default: () => [],
    },
    contextPath: {
      type: String,
      default: '',
    },
    formActionUrl: {
      type: String,
      default: '',
    },
    locale: {
      type: String,
      default: '',
    },
    reCaptchaSiteKey: {
      type: String,
      default: '',
    },
    reCaptchaTokenToggle: {
      type: String,
      default: '',
    },
    privacyPolicyText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      serialNumberField: this.selectedBike.serialNumber,
      shopNameField: '',
      firstNameField: '',
      lastNameField: '',
      emailAddressField: '',
      phoneNumberField: '',
      addressLine1Field: '',
      addressLine2Field: '',
      townCityField: '',
      stateField: '',
      countryField: '',
      countriesList: [],
      purchaseDate: '',
      postalCodeField: '',
      trekRetailerField: '',
      datePickerContainerClass: '',
      countriesContainerClass: '',
      originLocation: window.location.origin,
      optInVal: 'false',
      hasAcceptedPrivacyPolicy: true,
      hasRecaptchaFailure: false,
      hasRecaptchaToggled: false,
      reCaptchaToken: '',
    };
  },
  computed: {
    ignoreRedirectUrl() {
      return `${this.originLocation}${this.contextPath}/${
        this.b2b ? 'bike-registration' : 'productRegistration'
      }/confirmation`;
    },
    isPrivacyPolicyLocale() {
      return !this.b2b && (this.locale === 'es' || this.locale === 'pt');
    },
    ...mapState('backend', ['b2b']),
  },
  mounted() {
    // populate countries data when base properties are available (they return undefined if set in data())
    this.countryField = this.getBaseProperty('pleaseSelectText');
    this.countriesList = [this.getBaseProperty('pleaseSelectText'), ...this.countries];
    // disables register button for locales that require privacy policy accept to register
    if (this.isPrivacyPolicyLocale) this.hasAcceptedPrivacyPolicy = false;
    this.hasRecaptchaToggled = this.reCaptchaTokenToggle === 'true';
  },
  methods: {
    setDatePicker({event}) {
      if (event) this.purchaseDate = event;
      this.datePickerContainerClass = '';
    },
    checkDatePicker() {
      if (this.purchaseDate === '') {
        this.datePickerContainerClass = 'has-error';
        return false;
      } else {
        this.datePickerContainerClass = '';
        return true;
      }
    },
    fieldHasError(nameOfField) {
      return !!this.$validator.errors.items.find((o) => o.field == nameOfField);
    },
    checkCountryField() {
      if (!this.countryField || this.countryField === this.getBaseProperty('pleaseSelectText')) {
        this.countriesContainerClass = 'has-error';
        return false;
      } else {
        this.countriesContainerClass = '';
        return true;
      }
    },
    executeRecaptcha() {
      if (!this.hasRecaptchaToggled) this.validateAndSubmit();
      this.$refs.recaptcha.execute();
    },
    tokenFailure() {
      this.$notify({
        type: 'alert',
        message: this.bp('unableToProcess'),
      });
      this.hasRecaptchaFailure = true;
      console.log('recaptcha has failed');
    },
    validateAndSubmit(event) {
      if (event) this.reCaptchaToken = event;
      this.$validator.validateAll().then((result) => {
        this.checkCountryField(); // Make it 'red' if not selected
        if (this.checkDatePicker() && this.checkCountryField() && result) this.$refs.productRegistrationForm.submit();
      });
    },
  },
};
</script>
